import { useEffect, useMemo, useState } from "react";
import { Check, RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useSilver } from "@/context/SilverContext";
import { RETIREMENT_STAGES, BEDROOMS, SIGNALS, MOSAIC_GROUPS } from "@/data/mosaic";
import { EMPTY_FILTERS, countForFilters, groupMatchesFilters } from "@/lib/audienceEngine";
import { AnimatedNumber } from "@/components/silver/common/AnimatedNumber";
import { cn } from "@/lib/utils";

function Option({ checked, onToggle, label, testId }) {
  return (
    <button type="button" role="checkbox" aria-checked={checked} onClick={onToggle}
      className={cn("flex w-full items-center gap-3 rounded-2xl border px-4 py-3.5 text-left text-[14px] font-medium transition-all duration-300", checked ? "border-[#96C11F]/60 bg-[#96C11F]/10 text-white" : "border-white/10 bg-white/[0.03] text-white/75 hover:border-white/25")}
      data-testid={testId}>
      <span className={cn("grid h-5 w-5 shrink-0 place-items-center rounded-md border transition-colors", checked ? "border-[#96C11F] bg-[#96C11F] text-[#12290F]" : "border-white/25")}>
        {checked && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      </span>
      {label}
    </button>
  );
}

function Group({ title, hint, children }) {
  return (
    <fieldset className="space-y-3">
      <legend className="mb-3 flex w-full items-baseline justify-between">
        <span className="eyebrow">{title}</span>
        <span className="font-ui text-[11px] text-white/40">{hint}</span>
      </legend>
      {children}
    </fieldset>
  );
}

const toggleIn = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v].sort());

export function FilterDrawer() {
  const { filterDrawerOpen, setFilterDrawerOpen, filters, setFilters, areas } = useSilver();
  const [draft, setDraft] = useState(filters);
  useEffect(() => { if (filterDrawerOpen) setDraft(filters); }, [filterDrawerOpen, filters]);

  const estimate = useMemo(() => (areas.length ? countForFilters(areas, draft) : 0), [areas, draft]);
  const profiles = useMemo(() => MOSAIC_GROUPS.filter((g) => groupMatchesFilters(g, draft)).length, [draft]);

  return (
    <Sheet open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
      <SheetContent side="right" className="flex w-full flex-col !rounded-none !rounded-l-[28px] border-l border-white/10 !bg-[rgba(12,30,16,0.94)] p-0 text-white backdrop-blur-2xl sm:max-w-[460px]" data-testid="filter-drawer">
        <SheetHeader className="px-7 pb-2 pt-8 text-left">
          <p className="eyebrow">Advanced</p>
          <SheetTitle className="text-3xl font-extrabold tracking-[-0.04em] text-white">Fine-tune audience</SheetTitle>
          <SheetDescription className="text-[14px] text-white/55">
            Options in the same group widen the audience (any of). Different groups narrow it (all of).
          </SheetDescription>
        </SheetHeader>

        <div className="thin-scroll flex-1 space-y-8 overflow-y-auto px-7 py-6">
          <Group title="Retirement stage" hint="Any of">
            <div className="grid grid-cols-2 gap-3">
              {RETIREMENT_STAGES.map((s) => (
                <Option key={s.key} label={s.label} checked={draft.retirement.includes(s.key)} onToggle={() => setDraft((d) => ({ ...d, retirement: toggleIn(d.retirement, s.key) }))} testId={`filter-stage-${s.key}`} />
              ))}
            </div>
          </Group>
          <Group title="Property" hint="Any of">
            <div className="grid grid-cols-2 gap-3">
              {BEDROOMS.map((b) => (
                <Option key={b} label={`${b} Bed`} checked={draft.property.includes(b)} onToggle={() => setDraft((d) => ({ ...d, property: toggleIn(d.property, b) }))} testId={`filter-beds-${b}`} />
              ))}
            </div>
          </Group>
          <Group title="Opportunity" hint={draft.opportunityMatch === "all" ? "All of" : "Any of"}>
            <div className="space-y-3">
              {SIGNALS.map((s) => (
                <Option key={s.key} label={s.label} checked={draft[s.key] === true} onToggle={() => setDraft((d) => ({ ...d, [s.key]: d[s.key] ? null : true }))} testId={`filter-signal-${s.key}`} />
              ))}
            </div>
            <div className="mt-4 flex items-center gap-1 rounded-full border border-white/10 bg-black/20 p-1" role="radiogroup" aria-label="Opportunity signal matching">
              {[["any", "Match any signal"], ["all", "Match all signals"]].map(([k, label]) => (
                <button key={k} type="button" role="radio" aria-checked={draft.opportunityMatch === k} onClick={() => setDraft((d) => ({ ...d, opportunityMatch: k }))}
                  className={cn("flex-1 rounded-full px-3 py-2 font-ui text-[12px] font-semibold transition-colors", draft.opportunityMatch === k ? "bg-[#96C11F] text-[#12290F]" : "text-white/60 hover:text-white")}
                  data-testid={`filter-match-${k}`}>
                  {label}
                </button>
              ))}
            </div>
          </Group>
        </div>

        <div className="border-t border-white/10 bg-black/20 px-7 pb-7 pt-5">
          <div className="flex items-end justify-between">
            <div>
              <p className="eyebrow-muted">Estimated audience</p>
              <p className="mt-1 flex items-baseline gap-2">
                <AnimatedNumber value={estimate} duration={0.7} className="num text-4xl font-extrabold tracking-[-0.04em] text-white" data-testid="filter-drawer-estimate" />
                <span className="text-[14px] text-white/50">households</span>
              </p>
            </div>
            <p className="pb-1 font-ui text-[12px] text-white/45">{profiles} of {MOSAIC_GROUPS.length} profiles</p>
          </div>
          {!areas.length && <p className="mt-2 text-[12px] text-white/40">Add a postcode district to see live numbers.</p>}
          <div className="mt-5 flex gap-3">
            <button type="button" className="btn-ghost" onClick={() => setDraft({ ...EMPTY_FILTERS })} data-testid="filter-reset-btn">
              <RotateCcw className="h-4 w-4" /> Reset
            </button>
            <button type="button" className="btn-primary flex-1" onClick={() => { setFilters(draft); setFilterDrawerOpen(false); }} data-testid="filter-apply-btn">
              Apply audience
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
